import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import useLanguage from '../../hooks/useLanguage';
import useTheme from '../../hooks/useTheme';
import AppRail from './AppRail';
import { isImmersiveRoute } from './immersiveRoutes';
import { SystemMessageCenterProvider } from './SystemMessageCenter';
import { getCurrentTeam } from '../account/teamApi';
import { TeamBrandProvider } from '../branding/TeamBrandContext';

export default function VNextLayout({ preview = false, accountOverlay = false }) {
  const [user, setUser] = useState(preview ? { username: '小影创作者', credits: 1280 } : null);
  const [userLoading, setUserLoading] = useState(!preview);
  const [currentTeam, setCurrentTeam] = useState(null);
  const userRequestRef = useRef(0);
  const teamRequestRef = useRef(0);
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const immersive = isImmersiveRoute(location.pathname);

  const fetchUser = useCallback(async () => {
    const requestId = ++userRequestRef.current;
    if (preview) {
      setUser({ username: '小影创作者', credits: 1280 });
      setUserLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setUserLoading(false);
      return;
    }
    if (token === 'local-dev-mock-token') {
      setUser({ username: '本地测试', credits: 9999 });
      setUserLoading(false);
      return;
    }

    try {
      const response = await api.get('/users/me');
      if (requestId !== userRequestRef.current || token !== localStorage.getItem('token')) return;
      setUser(response.data);
    } catch {
      if (requestId === userRequestRef.current && token === localStorage.getItem('token')) setUser(null);
    } finally {
      if (requestId === userRequestRef.current && token === localStorage.getItem('token')) setUserLoading(false);
    }
  }, [preview]);

  const fetchCurrentTeam = useCallback(async () => {
    const requestId = ++teamRequestRef.current;
    if (preview) {
      setCurrentTeam(null);
      return null;
    }
    const token = localStorage.getItem('token');
    if (!token || token === 'local-dev-mock-token') {
      setCurrentTeam(null);
      return null;
    }
    try {
      const team = await getCurrentTeam();
      if (requestId !== teamRequestRef.current || token !== localStorage.getItem('token')) return null;
      setCurrentTeam(team || null);
      return team || null;
    } catch {
      if (requestId === teamRequestRef.current && token === localStorage.getItem('token')) setCurrentTeam(null);
      return null;
    }
  }, [preview]);

  useEffect(() => {
    fetchUser();
    fetchCurrentTeam();
    const timer = localStorage.getItem('token')
      ? window.setInterval(() => {
          fetchUser();
          fetchCurrentTeam();
        }, 30000)
      : null;
    return () => {
      window.clearInterval(timer);
      userRequestRef.current += 1;
      teamRequestRef.current += 1;
    };
  }, [fetchCurrentTeam, fetchUser]);

  useEffect(() => {
    const refresh = event => {
      if (event.type === 'auth-changed' || event.type === 'team-changed') setCurrentTeam(null);
      if (event.type === 'auth-changed') setUser(null);
      fetchUser();
      fetchCurrentTeam();
    };
    window.addEventListener('credits-updated', refresh);
    window.addEventListener('auth-changed', refresh);
    window.addEventListener('team-changed', refresh);
    window.addEventListener('team-context-refreshed', refresh);
    return () => {
      window.removeEventListener('credits-updated', refresh);
      window.removeEventListener('auth-changed', refresh);
      window.removeEventListener('team-changed', refresh);
      window.removeEventListener('team-context-refreshed', refresh);
    };
  }, [fetchCurrentTeam, fetchUser]);

  useEffect(() => {
    if (!preview && !user && !userLoading) fetchUser();
    // Route changes provide a cheap recovery point after a transient user request failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, preview]);

  return (
    <TeamBrandProvider user={user} team={currentTeam}>
      <SystemMessageCenterProvider
        user={user}
        preview={preview}
        english={language === 'en-US'}
        onCreditsChanged={fetchUser}
      >
        <div className={`flex ${accountOverlay ? 'h-full' : 'h-screen'} overflow-hidden bg-[var(--vnext-page)] text-[var(--vnext-ink)]`}>
          {!immersive && !accountOverlay && (
            <AppRail
              user={user}
              currentTeam={currentTeam}
              theme={theme}
              setTheme={setTheme}
              language={language}
              setLanguage={setLanguage}
              preview={preview}
              refreshUser={fetchUser}
              refreshTeam={fetchCurrentTeam}
            />
          )}
          {/* 移动端 AppRail 是底部栏，主区留了 pb-20 给它让位；
              隐藏导航栏时这段留白也要收掉，否则画布底部会空一条。 */}
          <main className={`${accountOverlay ? 'h-full' : 'h-screen'} min-w-0 flex-1 overflow-auto ${immersive || accountOverlay ? '' : 'pb-20 sm:pb-0'}`}>
            <Outlet context={{ user, userLoading, currentTeam, refreshUser: fetchUser, refreshTeam: fetchCurrentTeam, preview, accountOverlay, theme, setTheme, language, setLanguage }} />
          </main>
        </div>
      </SystemMessageCenterProvider>
    </TeamBrandProvider>
  );
}
